import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NavigationProvider } from "./contexts/NavigationContext";
import { GenerationProvider } from "./contexts/GenerationRegistry";
import { ConversationRouter } from "./components/ConversationRouter";
import { Topbar } from "./components/layout/Topbar";
import { Sidebar } from "./components/layout/Sidebar";
import { MainContent } from "./components/layout/MainContent";
import { UrlSync } from "./components/UrlSync";
import LoginPage from "./pages/LoginPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import NotFound from "./pages/NotFound";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import SharedConversationPage from "./pages/SharedConversationPage";

const queryClient = new QueryClient();

const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="animate-pulse text-secondary text-sm">Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/** Single authenticated shell — one NavigationProvider for all app routes */
const AuthenticatedApp = () => (
  <NavigationProvider>
    <GenerationProvider>
      <UrlSync />
      <Outlet />
      <div className="h-screen w-full bg-surface flex flex-col overflow-hidden">
        <Sidebar />
        <Topbar />
        <MainContent />
      </div>
    </GenerationProvider>
  </NavigationProvider>
);

/** Home: public login or redirect to authenticated shell */
const HomePage = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="animate-pulse text-secondary text-sm">Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return <Navigate to="/app" replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <Analytics />
      <SpeedInsights />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacidade" element={<PrivacyPolicyPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route
              path="/share/:shareToken"
              element={
                <AuthGuard>
                  <SharedConversationPage />
                </AuthGuard>
              }
            />
            <Route
              path="/app"
              element={
                <AuthGuard>
                  <AuthenticatedApp />
                </AuthGuard>
              }
            >
              <Route index element={null} />
              <Route path="chat/:conversationId" element={<ConversationRouter />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
